import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Users, Plus, Trash2, Calendar } from "lucide-react"
import { Button } from "../components/ui/button.tsx"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog.tsx"
import { Input } from "../components/ui/input.tsx"
import { Label } from "../components/ui/label.tsx"
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group.tsx"
import { ProfileLayout } from "../components/ProfileLayout"
import { useEffect } from "react"
import { getCookie } from "../utils/cookies"
import api from "../utils/api"

// Helper function to convert string to Title Case
function toTitleCase(str) {
  if (!str) return '';
  return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

export default function FamilyMembersPage() {
  const navigate = useNavigate()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [members, setMembers] = useState([])
  const [relations, setRelations] = useState([])
  const [states, setStates] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get("/apis/web/relations")
      .then(res => {
        if (res.data?.success && Array.isArray(res.data.data?.records)) {
          setRelations(res.data.data.records.map(r => ({ id: r.RELATION_ID, name: r.RELATION_NAME })))
        }
      })
      .catch(err => console.error("Error fetching relations:", err))
    api.get("/apis/web/states")
      .then(res => {
        if (res.data?.success && Array.isArray(res.data.data)) {
          setStates(res.data.data.map(s => ({ id: s.state_id, name: s.state_name })))
        }
      })
      .catch(err => console.error("Error fetching states:", err))
  }, [])

  useEffect(() => {
    const userStr = localStorage.getItem("user") || getCookie("user");
    
    if (!userStr || userStr === "undefined") {
      navigate("/");
      return;
    }

  const fetchPatient = async () => {
    try {
      const user = JSON.parse(userStr);
      let mobile = user?.mobile || user?.phone || user?.mobileNo || "";
      if (!mobile) return;
      mobile = mobile.replace(/\D/g, "").replace(/^91/, "")

      const response = await api.get(`/apis/web/patientbymobile?mobile=91${mobile}`, {
        headers: {
          "Content-Type": "application/json",
          "middleware-key": "$2b$10$5FnYj95ETCkrL3gK6p4zIeqigdvkpP8In5x.8K009CbaDgMc4G7VG"
        }
      })

      const data = response.data

      if (data?.status === 0 && Array.isArray(data.result)) {
        const formatted = data.result.map((item, index) => ({
          id: index + 1,
          name: toTitleCase(item.patient_name?.trim() || ""),
          mrNo: item.patientmr_no || "",
          age: item.age || "",
          gender: item.gender === "M" ? "Male" : item.gender === "F" ? "Female" : item.gender || "",
          dob: item.patient_dob || "",
          email: item.email || "",
          mobile: item.mobile || ""
        }))

        setMembers(formatted)
      }
    } catch (error) {
      console.error("Error fetching patient data:", error)
    } finally {
      setLoading(false)
    }
  }

  fetchPatient()
}, [navigate])


  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    dob: "",
    relation: "",
    gender: "",
    stateId: ""
  })

  const handleLogout = () => {
    localStorage.removeItem("user")
    navigate("/")
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.firstName.trim()) { alert("Please enter first name"); return }
    if (!formData.lastName.trim()) { alert("Please enter last name"); return }
    if (!formData.dob) { alert("Please select date of birth"); return }
    if (!formData.relation) { alert("Please select relation"); return }
    if (!formData.stateId) { alert("Please select state"); return }
    if (!formData.gender) { alert("Please select gender"); return }
    setLoading(true)
    try {
      const userStr = localStorage.getItem("user") || getCookie("user")
      const user = userStr ? JSON.parse(userStr) : {}
      let mobile = user?.mobile || user?.phone || user?.mobileNo || ""
      mobile = mobile.replace(/\D/g, "").replace(/^91/, "")

      const genderMap = { Male: "M", Female: "F", Other: "O" }
      const response = await api.post("/apis/web/registerpatient", {
        FirstName: formData.firstName,
        LastName: formData.lastName,
        Dob: formData.dob,
        Gender: genderMap[formData.gender] || formData.gender,
        Mobile: mobile,
        Relation: formData.relation,
        stateId: formData.stateId,
        CountryMobileCode: "+91",
        country: "India",
      })

      const data = response.data
      if (data.success) {
        // Refresh the members list from API
        const patientRes = await api.get(`/apis/web/patientbymobile?mobile=91${mobile}`, {
          headers: {
            "Content-Type": "application/json",
            "middleware-key": "$2b$10$5FnYj95ETCkrL3gK6p4zIeqigdvkpP8In5x.8K009CbaDgMc4G7VG"
          }
        })
        if (patientRes.data?.status === 0 && Array.isArray(patientRes.data.result)) {
          setMembers(patientRes.data.result.map((item, index) => ({
            id: index + 1,
            name: toTitleCase(item.patient_name?.trim() || ""),
            mrNo: item.patientmr_no || "",
            age: item.age || "",
            gender: item.gender === "M" ? "Male" : item.gender === "F" ? "Female" : item.gender || "",
            dob: item.patient_dob || "",
            email: item.email || "",
            mobile: item.mobile || ""
          })))
        }
        setFormData({ firstName: "", lastName: "", dob: "", relation: "", gender: "", stateId: "" })
        setIsDialogOpen(false)
      } else {
        alert(data.message || "Failed to add member")
      }
    } catch (error) {
      console.error("Error adding member:", error)
      alert("Failed to add member")
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = (id) => {
    setMembers(members.filter(member => member.id !== id))
  }

  return (
    <ProfileLayout onLogout={handleLogout}>
      <div className="bg-gray-50 min-h-screen">
        <div className="bg-white shadow-sm border-b border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6 text-red-600" />
              <h1 className="text-2xl font-bold text-gray-900">Family Details</h1>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
              <Button className="bg-red-700 hover:bg-red-800 gap-2 w-full sm:w-auto" onClick={() => setIsDialogOpen(true)}>
                <Plus className="w-4 h-4" />
                Add New Member
              </Button>
              <Button className="gap-2 w-full sm:w-auto" onClick={() => navigate("/book-now")}>
                <Calendar className="w-4 h-4" />
                Book an Appointment
              </Button>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 max-w-5xl">

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading members...</p>
          </div>
        ) : members.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12">
            <div className="flex flex-col items-center justify-center text-center">
              <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mb-4">
                <Users className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold mb-2">No Members Found</h2>
              <p className="text-gray-600 mb-6">You haven't added any family members yet.</p>
              <Button className="bg-red-700 hover:bg-red-800" onClick={() => setIsDialogOpen(true)}>
                Add New Member
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {members.map((member) => {
              const initial = member.name.charAt(0).toUpperCase()
              return (
                <div key={member.id} className="bg-white rounded-lg shadow-sm p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4 w-full sm:w-auto">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-200 flex items-center justify-center text-xl sm:text-2xl font-semibold text-gray-700 flex-shrink-0">
                      {initial}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900">{member.name}</h3>
                      <p className="text-sm text-gray-600">{member.mrNo} | {member.gender} | {member.age}</p>
                    </div>
                  </div>
                  {/* <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                    <button className="text-gray-600 hover:text-gray-900">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(member.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div> */}
                </div>
              )
            })}
          </div>
        )}
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Family Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>First Name <span className="text-red-600">*</span></Label>
                <Input
                  required
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={(e) => setFormData({...formData, firstName: e.target.value.replace(/[^a-zA-Z\s]/g, '')})}
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name <span className="text-red-600">*</span></Label>
                <Input
                  required
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={(e) => setFormData({...formData, lastName: e.target.value.replace(/[^a-zA-Z\s]/g, '')})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Date of Birth <span className="text-red-600">*</span></Label>
              <Input
                required
                type={formData.dob ? "date" : "text"}
                onFocus={(e) => e.target.type = "date"}
                onBlur={(e) => { if (!e.target.value) e.target.type = "text" }}
                placeholder="Date of Birth"
                value={formData.dob}
                onChange={(e) => setFormData({...formData, dob: e.target.value})}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="space-y-2">
              <Label>Relation <span className="text-red-600">*</span></Label>
              <select
                required
                value={formData.relation}
                onChange={(e) => setFormData({...formData, relation: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-700"
              >
                <option value="">Select Relation</option>
                {relations.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>State <span className="text-red-600">*</span></Label>
              <select
                required
                value={formData.stateId}
                onChange={(e) => setFormData({...formData, stateId: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-700"
              >
                <option value="">Select State</option>
                {states.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Gender <span className="text-red-600">*</span></Label>
              <RadioGroup required value={formData.gender} onValueChange={(value) => setFormData({...formData, gender: value})}>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Male" id="male" />
                    <Label htmlFor="male" className="cursor-pointer">Male</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Female" id="female" />
                    <Label htmlFor="female" className="cursor-pointer">Female</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Other" id="other" />
                    <Label htmlFor="other" className="cursor-pointer">Other</Label>
                  </div>
                </div>
              </RadioGroup>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-red-700 hover:bg-red-800">
                Add Member
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </ProfileLayout>
  )
}
